import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiShield, FiAlertTriangle, FiCheckCircle, FiBarChart2, FiTrendingUp, FiSliders, FiEdit } from '../utils/icons';
import Layout from '../components/layout/Layout';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { useUserData } from '../utils/UserDataContext';
import Modal from '../components/modals/Modal';
import FinanceForm from '../components/forms/FinanceForm';

const RiskAssessment: React.FC = () => {
  const { userData, updateRiskProfile } = useUserData();
  const { riskProfile } = userData.aiInsights;
  
  // Modal states
  const [isRiskProfileModalOpen, setIsRiskProfileModalOpen] = useState(false);
  const [isInvestmentGoalsModalOpen, setIsInvestmentGoalsModalOpen] = useState(false);
  const [isPreferredInvestmentsModalOpen, setIsPreferredInvestmentsModalOpen] = useState(false);
  
  // Risk level descriptions
  const riskLevelDescriptions = {
    low: 'You prefer stability and security over high returns. You are risk-averse and prioritize preserving your capital.',
    medium: 'You seek a balance between growth and security. You accept moderate fluctuations for the potential of better returns.',
    high: 'You prioritize growth and are comfortable with significant market fluctuations for potentially higher returns.'
  };
  
  // Investment horizon descriptions
  const horizonDescriptions = {
    short: 'Less than 3 years. You plan to use these funds in the near future.',
    medium: '3-7 years. You have a moderate timeline for using these investments.',
    long: 'More than 7 years. You\'re investing for long-term goals like retirement.'
  };

  // Handle risk profile update
  const handleRiskProfileUpdate = (formData: any) => {
    updateRiskProfile({
      riskTolerance: formData.riskTolerance,
      investmentHorizon: formData.investmentHorizon,
      financialSituation: {
        emergencyFund: formData.emergencyFund === 'true',
        debtLevel: formData.debtLevel,
        incomeStability: formData.incomeStability
      }
    });
    setIsRiskProfileModalOpen(false);
  };

  // Handle investment goals update
  const handleInvestmentGoalsUpdate = (formData: any) => {
    const goals = Object.keys(formData)
      .filter(key => key.startsWith('goal-') && formData[key] === true)
      .map(key => key.replace('goal-', ''));
    
    updateRiskProfile({
      investmentGoals: goals
    });
    setIsInvestmentGoalsModalOpen(false);
  };

  // Handle preferred investments update
  const handlePreferredInvestmentsUpdate = (formData: any) => {
    const investments = Object.keys(formData)
      .filter(key => key.startsWith('investment-') && formData[key] === true)
      .map(key => key.replace('investment-', ''));
    
    updateRiskProfile({
      preferredInvestments: investments
    });
    setIsPreferredInvestmentsModalOpen(false);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background animate-fadeIn">
        <div className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold mb-2">Risk Assessment</h1>
            <p className="text-text/70">Understand your financial risk profile and get personalized investment recommendations</p>
          </div>

          {/* Risk Profile Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <Card variant="glass" className="lg:col-span-2 p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-lg font-semibold">Your Risk Profile</h2>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setIsRiskProfileModalOpen(true)}
                >
                  <FiEdit className="mr-2" size={16} /> Update Profile
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-text/70">Risk Tolerance</h3>
                    <div className="mt-2 flex items-center">
                      <div className="w-full bg-border rounded-full h-2 mr-4">
                        <div 
                          className="bg-gradient-to-r from-primary to-accent rounded-full h-2 animate-shimmer"
                          style={{ 
                            width: riskProfile.riskTolerance === 'low' ? '33%' : 
                                 riskProfile.riskTolerance === 'medium' ? '66%' : '100%' 
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium capitalize">{riskProfile.riskTolerance}</span>
                    </div>
                    <p className="mt-2 text-sm text-text/70">{riskLevelDescriptions[riskProfile.riskTolerance]}</p>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-text/70">Investment Horizon</h3>
                    <div className="mt-2 flex items-center">
                      <div className="w-full bg-border rounded-full h-2 mr-4">
                        <div 
                          className="bg-gradient-to-r from-primary to-accent rounded-full h-2 animate-shimmer"
                          style={{ 
                            width: riskProfile.investmentHorizon === 'short' ? '33%' : 
                                 riskProfile.investmentHorizon === 'medium' ? '66%' : '100%'
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium capitalize">{riskProfile.investmentHorizon}</span>
                    </div>
                    <p className="mt-2 text-sm text-text/70">{horizonDescriptions[riskProfile.investmentHorizon]}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-text/70 mb-2">Financial Situation</h3>
                    <ul className="space-y-2">
                      <li className="flex items-center">
                        <span className={`w-3 h-3 rounded-full mr-2 ${riskProfile.financialSituation.emergencyFund ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        <span className="text-sm">Emergency Fund: {riskProfile.financialSituation.emergencyFund ? 'Yes' : 'No'}</span>
                      </li>
                      <li className="flex items-center">
                        <span className={`w-3 h-3 rounded-full mr-2 ${
                          riskProfile.financialSituation.debtLevel === 'low' ? 'bg-green-500' :
                          riskProfile.financialSituation.debtLevel === 'medium' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></span>
                        <span className="text-sm">Debt Level: <span className="capitalize">{riskProfile.financialSituation.debtLevel}</span></span>
                      </li>
                      <li className="flex items-center">
                        <span className={`w-3 h-3 rounded-full mr-2 ${
                          riskProfile.financialSituation.incomeStability === 'high' ? 'bg-green-500' :
                          riskProfile.financialSituation.incomeStability === 'medium' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></span>
                        <span className="text-sm">Income Stability: <span className="capitalize">{riskProfile.financialSituation.incomeStability}</span></span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </Card>

            <Card variant="glass" className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-primary/10 mx-auto flex items-center justify-center mb-4">
                  <FiShield className="text-primary" size={32} />
                </div>
                <h2 className="text-lg font-semibold">Risk Score</h2>
                <div className="text-4xl font-bold mt-2">
                  {riskProfile.riskTolerance === 'low' ? '3.2' : 
                   riskProfile.riskTolerance === 'medium' ? '6.7' : '8.5'}/10
                </div>
                <p className="text-sm text-text/70 mt-2">
                  {riskProfile.riskTolerance === 'low' ? 'Conservative' : 
                   riskProfile.riskTolerance === 'medium' ? 'Moderate' : 'Aggressive'} Investor
                </p>
              </div>
              <Button 
                fullWidth 
                variant="outline"
                rightIcon={<FiArrowRight size={16} />}
              >
                View Detailed Analysis
              </Button>
            </Card>
          </div>

          {/* Investment Goals & Preferences */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <Card variant="glass" className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-lg font-semibold">Investment Goals</h2>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setIsInvestmentGoalsModalOpen(true)}
                >
                  <FiEdit className="mr-2" size={16} /> Edit
                </Button>
              </div>
              
              {riskProfile.investmentGoals.length > 0 ? (
                <ul className="space-y-3">
                  {riskProfile.investmentGoals.map((goal, index) => (
                    <li key={index} className="flex items-center bg-card/60 rounded-lg p-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                        <span className="text-primary font-semibold">{index + 1}</span>
                      </div>
                      <span className="font-medium">{goal}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-8 text-text/50">
                  No investment goals defined yet. Click 'Edit' to add your goals.
                </div>
              )}
            </Card>

            <Card variant="glass" className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-lg font-semibold">Preferred Investments</h2>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setIsPreferredInvestmentsModalOpen(true)}
                >
                  <FiEdit className="mr-2" size={16} /> Edit
                </Button>
              </div>
              
              {riskProfile.preferredInvestments.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {riskProfile.preferredInvestments.map((investment, index) => (
                    <span 
                      key={index} 
                      className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                    >
                      {investment}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-text/50">
                  No preferred investments defined yet. Click 'Edit' to add your preferences.
                </div>
              )}
            </Card>
          </div>

          {/* Recommendations */}
          <Card variant="glass" className="p-6 mb-8">
            <h2 className="text-lg font-semibold mb-6">Investment Recommendations</h2>
            
            <div className="space-y-4">
              {riskProfile.riskTolerance === 'low' && (
                <>
                  <div className="p-4 border border-border rounded-lg bg-card/60">
                    <h3 className="font-semibold mb-2">Conservative Portfolio Mix</h3>
                    <p className="text-sm mb-4">Based on your low risk tolerance, we recommend a conservative asset allocation:</p>
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      <div className="bg-blue-500/20 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-blue-500">60%</div>
                        <div className="text-xs">Bonds</div>
                      </div>
                      <div className="bg-green-500/20 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-green-500">20%</div>
                        <div className="text-xs">Stocks</div>
                      </div>
                      <div className="bg-yellow-500/20 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-yellow-500">15%</div>
                        <div className="text-xs">Cash</div>
                      </div>
                      <div className="bg-purple-500/20 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-purple-500">5%</div>
                        <div className="text-xs">Other</div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {riskProfile.riskTolerance === 'medium' && (
                <>
                  <div className="p-4 border border-border rounded-lg bg-card/60">
                    <h3 className="font-semibold mb-2">Balanced Portfolio Mix</h3>
                    <p className="text-sm mb-4">Based on your moderate risk tolerance, we recommend a balanced asset allocation:</p>
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      <div className="bg-blue-500/20 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-blue-500">40%</div>
                        <div className="text-xs">Bonds</div>
                      </div>
                      <div className="bg-green-500/20 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-green-500">45%</div>
                        <div className="text-xs">Stocks</div>
                      </div>
                      <div className="bg-yellow-500/20 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-yellow-500">10%</div>
                        <div className="text-xs">Cash</div>
                      </div>
                      <div className="bg-purple-500/20 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-purple-500">5%</div>
                        <div className="text-xs">Other</div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {riskProfile.riskTolerance === 'high' && (
                <>
                  <div className="p-4 border border-border rounded-lg bg-card/60">
                    <h3 className="font-semibold mb-2">Growth Portfolio Mix</h3>
                    <p className="text-sm mb-4">Based on your high risk tolerance, we recommend a growth-oriented asset allocation:</p>
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      <div className="bg-blue-500/20 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-blue-500">15%</div>
                        <div className="text-xs">Bonds</div>
                      </div>
                      <div className="bg-green-500/20 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-green-500">70%</div>
                        <div className="text-xs">Stocks</div>
                      </div>
                      <div className="bg-yellow-500/20 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-yellow-500">5%</div>
                        <div className="text-xs">Cash</div>
                      </div>
                      <div className="bg-purple-500/20 p-3 rounded-lg text-center">
                        <div className="text-lg font-bold text-purple-500">10%</div>
                        <div className="text-xs">Other</div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Risk Profile Modal */}
          <Modal
            isOpen={isRiskProfileModalOpen}
            onClose={() => setIsRiskProfileModalOpen(false)}
            title="Update Risk Profile"
            size="md"
          >
            <FinanceForm
              title="Your Risk Profile"
              description="Update your risk tolerance and financial situation to get more accurate recommendations."
              fields={[
                {
                  name: 'riskTolerance',
                  label: 'Risk Tolerance',
                  type: 'select',
                  required: true,
                  defaultValue: riskProfile.riskTolerance,
                  options: [
                    { value: 'low', label: 'Low - I prefer stability over higher returns' },
                    { value: 'medium', label: 'Medium - I balance between growth and security' },
                    { value: 'high', label: 'High - I aim for maximum growth and accept volatility' }
                  ]
                },
                {
                  name: 'investmentHorizon',
                  label: 'Investment Horizon',
                  type: 'select',
                  required: true,
                  defaultValue: riskProfile.investmentHorizon,
                  options: [
                    { value: 'short', label: 'Short - Less than 3 years' },
                    { value: 'medium', label: 'Medium - 3 to 7 years' },
                    { value: 'long', label: 'Long - More than 7 years' }
                  ]
                },
                {
                  name: 'emergencyFund',
                  label: 'Do you have an emergency fund?',
                  type: 'select',
                  required: true,
                  defaultValue: riskProfile.financialSituation.emergencyFund ? 'true' : 'false',
                  options: [
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' }
                  ]
                },
                {
                  name: 'debtLevel',
                  label: 'Debt Level',
                  type: 'select',
                  required: true,
                  defaultValue: riskProfile.financialSituation.debtLevel,
                  options: [
                    { value: 'low', label: 'Low - Little to no debt' },
                    { value: 'medium', label: 'Medium - Manageable debt' },
                    { value: 'high', label: 'High - Significant debt burden' }
                  ]
                },
                {
                  name: 'incomeStability',
                  label: 'Income Stability',
                  type: 'select',
                  required: true,
                  defaultValue: riskProfile.financialSituation.incomeStability,
                  options: [
                    { value: 'low', label: 'Low - Unpredictable or variable income' },
                    { value: 'medium', label: 'Medium - Mostly stable with some variation' },
                    { value: 'high', label: 'High - Consistent, reliable income' }
                  ]
                }
              ]}
              submitLabel="Update Profile"
              onSubmit={handleRiskProfileUpdate}
            />
          </Modal>

          {/* Investment Goals Modal */}
          <Modal
            isOpen={isInvestmentGoalsModalOpen}
            onClose={() => setIsInvestmentGoalsModalOpen(false)}
            title="Update Investment Goals"
            size="md"
          >
            <FinanceForm
              title="Investment Goals"
              description="Select your primary investment goals."
              fields={[
                {
                  name: 'goal-Retirement',
                  label: 'Retirement',
                  type: 'checkbox',
                  defaultValue: riskProfile.investmentGoals.includes('Retirement'),
                },
                {
                  name: 'goal-Home Purchase',
                  label: 'Home Purchase',
                  type: 'checkbox',
                  defaultValue: riskProfile.investmentGoals.includes('Home Purchase'),
                },
                {
                  name: 'goal-Education',
                  label: 'Education Funding',
                  type: 'checkbox',
                  defaultValue: riskProfile.investmentGoals.includes('Education'),
                },
                {
                  name: 'goal-Building Wealth',
                  label: 'Building Wealth',
                  type: 'checkbox',
                  defaultValue: riskProfile.investmentGoals.includes('Building Wealth'),
                },
                {
                  name: 'goal-Income Generation',
                  label: 'Income Generation',
                  type: 'checkbox',
                  defaultValue: riskProfile.investmentGoals.includes('Income Generation'),
                },
                {
                  name: 'goal-Legacy Planning',
                  label: 'Legacy Planning',
                  type: 'checkbox',
                  defaultValue: riskProfile.investmentGoals.includes('Legacy Planning'),
                }
              ]}
              submitLabel="Update Goals"
              onSubmit={handleInvestmentGoalsUpdate}
            />
          </Modal>

          {/* Preferred Investments Modal */}
          <Modal
            isOpen={isPreferredInvestmentsModalOpen}
            onClose={() => setIsPreferredInvestmentsModalOpen(false)}
            title="Update Preferred Investments"
            size="md"
          >
            <FinanceForm
              title="Preferred Investment Types"
              description="Select the types of investments you prefer."
              fields={[
                {
                  name: 'investment-Stocks',
                  label: 'Stocks',
                  type: 'checkbox',
                  defaultValue: riskProfile.preferredInvestments.includes('Stocks'),
                },
                {
                  name: 'investment-Bonds',
                  label: 'Bonds',
                  type: 'checkbox',
                  defaultValue: riskProfile.preferredInvestments.includes('Bonds'),
                },
                {
                  name: 'investment-Index Funds',
                  label: 'Index Funds',
                  type: 'checkbox',
                  defaultValue: riskProfile.preferredInvestments.includes('Index Funds'),
                },
                {
                  name: 'investment-ETFs',
                  label: 'ETFs',
                  type: 'checkbox',
                  defaultValue: riskProfile.preferredInvestments.includes('ETFs'),
                },
                {
                  name: 'investment-Real Estate',
                  label: 'Real Estate',
                  type: 'checkbox',
                  defaultValue: riskProfile.preferredInvestments.includes('Real Estate'),
                },
                {
                  name: 'investment-Cryptocurrency',
                  label: 'Cryptocurrency',
                  type: 'checkbox',
                  defaultValue: riskProfile.preferredInvestments.includes('Cryptocurrency'),
                },
                {
                  name: 'investment-Commodities',
                  label: 'Commodities',
                  type: 'checkbox',
                  defaultValue: riskProfile.preferredInvestments.includes('Commodities'),
                }
              ]}
              submitLabel="Update Preferences"
              onSubmit={handlePreferredInvestmentsUpdate}
            />
          </Modal>
        </div>
      </div>
    </Layout>
  );
};

export default RiskAssessment; 